import { useEffect, useState } from 'react';
import { FormControl, InputAdornment } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import FieldNames from '../../../defaults/FieldNames.json';
import { useSelector } from 'react-redux';
import BasicTextField from './BasicTextField';
import Events from '../../../defaults/Events';

const useStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid red',
        },
        '&&:after': {
            borderBottom: '2px solid red',
        },
    },
});

const useNotSavedStyles = makeStyles({
    underline: {
        '&&&:before': {
            borderBottom: '2px solid gold',
        },
        '&&:after': {
            borderBottom: '2px solid gold',
        },
    },
});

const theme = createTheme({
    overrides: {},
});

export default function DetailsText({
    detail,
    field,
    onFieldChange,
    isRequired,
    fieldIsStatic,
    getAllApprovalFieldsCount,
    unitTypeValue,
    selectionValue,
    isProject = false,
}) {
    const treatmentValidationUnsavedValues = useSelector((state) => {
        return state.treatmentValidation;
    });
    const activityValidationUnsavedValues = useSelector((state) => {
        return state.activityValidation;
    });
    const projectValidationUnsavedValues = useSelector((state) => {
        return state.projectValidation;
    });
    const actualsTreatmentValidationUnsavedValues = useSelector((state) => {
        return state.actualsTreatmentValidation;
    });
    const actualsActivityValidationUnsavedValues = useSelector((state) => {
        return state.actualsActivityValidation;
    });

    const classes = useStyles();
    const notsavedclasses = useNotSavedStyles();

    const [value, setValue] = useState('');

    const isWbsWithBLM = Boolean(
        field === FieldNames.featureFields.wbsID &&
        detail?.attributes?.[FieldNames.featureFields.fundingAgency]?.includes(
            FieldNames.agencies.blm
        )
    );

    const inputStartText = isWbsWithBLM ? (
        <InputAdornment position='start'>LF.HF.</InputAdornment>
    ) : null;

    const inputEndText = isWbsWithBLM ? (
        <InputAdornment position='end' sx={{ paddingRight: '25px' }}>
            0000
        </InputAdornment>
    ) : null;

    useEffect(() => {
        if (detail && detail.attributes) {
            //Need to put check for post fire here
            if (
                field === FieldNames.legacyIDFields.treatmentID ||
                field === FieldNames.legacyIDFields.activityID ||
                field === FieldNames.legacyIDFields.actualTreatmentID ||
                field === FieldNames.legacyIDFields.actualActivityID ||
                field === FieldNames.featureFields.entityID ||
                field === FieldNames.featureFields.id
            ) {
                if (detail.attributes[FieldNames.featureFields.id]) {
                    setValue(detail.attributes[FieldNames.featureFields.id]);
                } else {
                    setValue('');
                }
            } else if (
                detail.attributes[field] === null ||
                detail.attributes[field] === undefined ||
                field === FieldNames.featureFields.fundingDepartment ||
                field === FieldNames.featureFields.fundingAgency ||
                field === FieldNames.featureFields.fundingRegion ||
                field === FieldNames.featureFields.fundingUnit ||
                field === FieldNames.featureFields.fundingSubUnit ||
                field === FieldNames.featureFields.fundingTribe ||
                field === FieldNames.featureFields.fundingSource ||
                field === FieldNames.featureFields.fundingUnitType ||
                field === FieldNames.featureFields.fundingSourceProgram ||
                field === FieldNames.featureFields.fundingSourceCategory ||
                field === FieldNames.featureFields.fundingSourceSubCategory
            ) {
                if (selectionValue) {
                    setValue(selectionValue);
                } else {
                    setValue('');
                }
            } else if (field === FieldNames.featureFields.wbsID) {
                if (detail.attributes[field] === 'XXXXXXXXXXXX') {
                    setValue('');
                } else if (
                    detail.attributes[field] &&
                    detail.attributes[FieldNames.featureFields.fundingAgency] &&
                    detail.attributes[FieldNames.featureFields.fundingAgency].includes(
                        FieldNames.agencies.blm
                    )
                ) {
                    if (detail.attributes[field].length > 4) {
                        if (
                            detail.attributes[field].substring(0, 6) !== FieldNames.wbsPrefixes.blm
                        ) {
                            // Added Guard for switching to BLM agency
                            setValue('');
                        } else {
                            const prefixRemoved = detail.attributes[field].replace(
                                FieldNames.wbsPrefixes.blm,
                                ''
                            );
                            const suffixRemoved = prefixRemoved.slice(0, -4);
                            setValue(suffixRemoved);
                        }
                    } else {
                        setValue(detail.attributes[field]);
                    }
                } else {
                    setValue(detail.attributes[field]);
                }
            } else if (field === FieldNames.featureFields.calculatedArea) {
                const units = detail.attributes[FieldNames.featureFields.areaUnit]
                    ? detail.attributes[FieldNames.featureFields.areaUnit]
                    : unitTypeValue
                      ? unitTypeValue
                      : '';
                setValue(`${detail.attributes[field]} ${units}`);
            } else {
                setValue(detail.attributes[field]);
            }
        }
    }, [detail, field, unitTypeValue, selectionValue]);

    const inputValueMissing = () => {
        if (detail && detail.attributes) {
            if (
                !fieldIsStatic &&
                FieldNames &&
                (!detail.attributes[field] ||
                    (field === FieldNames.featureFields.wbsID &&
                        (detail.attributes[field] === 'XXXXXXXXXXXX' ||
                            detail.attributes[field] === '')) ||
                    (isWbsWithBLM && value.length !== 4) ||
                    ((field === FieldNames.featureFields.department ||
                        field === FieldNames.featureFields.agency ||
                        field === FieldNames.featureFields.region ||
                        field === FieldNames.featureFields.unit ||
                        field === FieldNames.featureFields.tribeName) &&
                        detail.attributes[field] &&
                        detail.attributes[field].toLowerCase() === 'n/a'))
            ) {
                return true;
            } else {
                return false;
            }
        } else {
            return true;
        }
    };

    const showNotSavedClasses = (field) => {
        var fieldState = false;
        if (isProject) {
            if (detail?.attributes) {
                // Projects
                if (projectValidationUnsavedValues === null || !field || field.length === 0) {
                    return false;
                }
                fieldState = projectValidationUnsavedValues[field];
            }
        } else {
            if (
                detail &&
                detail.attributes &&
                detail.attributes[FieldNames.featureFields.class] ===
                    FieldNames.classTypes.treatment
            ) {
                // Estimated Treatment
                if (treatmentValidationUnsavedValues === null || !field || field.length === 0) {
                    return false;
                }
                fieldState = treatmentValidationUnsavedValues[field];
            } else if (
                detail &&
                detail.attributes &&
                detail.attributes[FieldNames.featureFields.class] ===
                    FieldNames.classTypes.actualTreatment
            ) {
                // Actual Treatment
                if (
                    actualsTreatmentValidationUnsavedValues === null ||
                    !field ||
                    field.length === 0
                ) {
                    return false;
                }
                fieldState = actualsTreatmentValidationUnsavedValues[field];
            } else if (
                detail &&
                detail.attributes &&
                detail.attributes[FieldNames.featureFields.class] === FieldNames.classTypes.activity
            ) {
                // Estimated Activity
                if (activityValidationUnsavedValues === null || !field || field.length === 0) {
                    return false;
                }
                fieldState = activityValidationUnsavedValues[field];
            } else if (
                detail &&
                detail.attributes &&
                detail.attributes[FieldNames.featureFields.class] ===
                    FieldNames.classTypes.actualActivity
            ) {
                // Actual Activity
                if (
                    actualsActivityValidationUnsavedValues === null ||
                    !field ||
                    field.length === 0
                ) {
                    return false;
                }
                fieldState = actualsActivityValidationUnsavedValues[field];
            } else {
                return false;
            }
        }
        return fieldState;
    };

    const attributeFormatOverride = (field, value) => {
        if (
            field === FieldNames.featureFields.wbsID &&
            detail?.attributes?.[FieldNames.featureFields.fundingAgency]?.includes(
                FieldNames.agencies.blm
            ) &&
            value
        ) {
            // Entered value for BLM WBSID can only be 4 characters/digits
            const appendedValue = FieldNames.wbsPrefixes.blm + value.substring(0, 4) + '0000';
            return appendedValue;
        }

        return value;
    };

    return (
        <FormControl variant='standard' fullWidth>
            <ThemeProvider theme={theme}>
                <BasicTextField
                    detail={detail}
                    onFieldChange={onFieldChange}
                    field={field}
                    label=''
                    value={value}
                    setValue={setValue}
                    variant={'standard'}
                    placeholder={''}
                    useFullWidth={true}
                    helperText={
                        isWbsWithBLM && Boolean(value) && value.length < 4
                            ? 'Value must be 4 digits in length and may only include Letters and Numbers'
                            : isWbsWithBLM && Boolean(value) && value.length > 4
                              ? 'Value must be 4 digits in length and may only include Letters and Numbers'
                              : ''
                    }
                    isError={isWbsWithBLM && Boolean(value) && value.length < 4}
                    useMultiline={true}
                    isDisabled={
                        fieldIsStatic ||
                        (getAllApprovalFieldsCount &&
                            getAllApprovalFieldsCount(detail, isRequired)) ||
                        field === FieldNames.featureFields.fundingDepartment ||
                        field === FieldNames.featureFields.fundingAgency ||
                        field === FieldNames.featureFields.fundingRegion ||
                        field === FieldNames.featureFields.fundingUnit ||
                        field === FieldNames.featureFields.fundingSubUnit ||
                        field === FieldNames.featureFields.fundingTribe ||
                        field === FieldNames.featureFields.fundingUnitType ||
                        field === FieldNames.featureFields.fundingSource ||
                        field === FieldNames.featureFields.fundingSourceProgram ||
                        field === FieldNames.featureFields.fundingSourceCategory ||
                        field === FieldNames.featureFields.fundingSourceSubCategory
                    }
                    inputProps={{
                        ...(isRequired && inputValueMissing()
                            ? {
                                  InputProps: {
                                      classes,
                                      startAdornment: inputStartText,
                                      endAdornment: inputEndText,
                                  },
                              }
                            : showNotSavedClasses(field)
                              ? {
                                    InputProps: {
                                        classes: notsavedclasses,
                                        startAdornment: inputStartText,
                                        endAdornment: inputEndText,
                                    },
                                }
                              : {
                                    InputProps: {
                                        startAdornment: inputStartText,
                                        endAdornment: inputEndText,
                                    },
                                }),
                    }}
                    attributeFormatOverride={attributeFormatOverride}
                    triggerEvent={Events.TriggerFieldChange} // This is what actually feeds back into details.attributes
                />
            </ThemeProvider>
        </FormControl>
    );
}
